/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* =================================================================================================
 * UpsellYard "Recommended for You!" popup — only for a valid, addable variant
 * =================================================================================================
 *
 * HOW THE APP ACTUALLY FIRES (read from the live app script, not guessed:
 * https://upsellyard.merchantyard.com/js/integrations/script.js)
 *
 * The app never listens to the Add to Cart button, and never listens to the form. On an
 * interval it walks window.ADD_TO_CART_BUTTON_SELECTOR (snippets/upsellyard.liquid) and, for
 * each button found, adds the class `upsellyard-atc-button` to it and inserts a transparent
 * `div.upsellyard-atc-button-overlay` as the button's previous sibling, absolutely positioned
 * over it at z-index 9. The click listener lives on THAT OVERLAY:
 *
 *   overlay click -> preventDefault() -> UpsellYard.upsellInit()
 *                 -> builds #upsellyard-wrapper -> openPopup() adds the `opened` class
 *
 * and only when the shopper dismisses the popup does closeUpsellPopup() call the app's internal
 * replay, which finally does atcButton.click() -- that replayed click is the first moment the
 * theme's form submits and ProductForm.validate() runs at all.
 *
 * upsellInit() has exactly one call site in the whole app bundle: that overlay click handler.
 * The app's other hooks (its XMLHttpRequest.open and fetch wrappers) only refresh the cart
 * subtotal for its free-gift logic and never open the popup.
 *
 * So the shopper's click never reaches the theme's button: the overlay eats it. That is why
 * guarding the button's own click could not work -- with an unchosen colour the popup opened
 * from the overlay, and the theme's error only appeared later, off the replayed click.
 *
 * WHAT THIS DOES
 *
 * The overlay click is judged here, at window capture -- the first node in the capture path, so
 * ahead of the app's own listener on the overlay itself, whenever the app script happens to
 * load. The verdict is fail-closed: unless this can prove the click leads to a real, addable
 * variant, the app's handler is never reached, upsellInit() is never called, #upsellyard-wrapper
 * is never built and `opened` is never set. The popup is not opened and closed, and not hidden
 * with CSS -- it is never created.
 *
 * Provable-valid means, from the theme's own state:
 *   - the theme's Add to Cart button is not disabled (how this theme marks an unavailable or
 *     sold-out variant), and
 *   - the theme's own ProductForm.validate() passes -- the exact method handleSubmit() calls
 *     in main.js, on the exact element the theme uses, so there is no second notion of "valid";
 *     a product with no option selectors has nothing to fail and passes, and
 *   - the form carries a real numeric variant id. The theme renders input[name="id"] empty
 *     until a variant resolves and re-renders it from the server on every variant change
 *     (sections/main-product.liquid), so an empty value means no addable variant even if the
 *     selectors look answered.
 *
 * Valid -> this returns without touching the event. The app behaves exactly as it always did.
 *
 * Invalid -> the app's listener is cut off and the click is handed straight to the theme's own
 * button instead, so the form submits, handleSubmit() runs, validate() fails there and paints
 * its own "Choose a Color" prompt, and nothing is added. The theme's validation is used, never
 * replaced, and no message of ours is ever shown. A disabled button ignores that forwarded
 * click, which is the wanted outcome for a sold-out variant: no add, no popup.
 *
 * One listener. No MutationObserver, no interval, no timer, no polling, no CSS.
 */
(function () {
  var OVERLAY = '.upsellyard-atc-button-overlay';

  /**
   * Finds the theme's Add to Cart button that an app overlay is covering. The app inserts the
   * overlay as the button's previous sibling and tags the button `upsellyard-atc-button`.
   * @param {Element} overlay - The app's overlay element.
   * @returns {Element|null} The covered button.
   */
  function getCoveredButton(overlay) {
    var parent = overlay.parentElement;
    if (!parent) return null;
    return parent.querySelector('.upsellyard-atc-button, button[name="add"], [type="submit"]');
  }

  /**
   * Gets the element owning the theme's validate(). <sticky-atc> extends <product-form> but
   * holds no variant picker of its own, so it defers to the main one, as sticky-atc.js does.
   * @param {Element} form - The product form.
   * @returns {Element|null} Element exposing validate().
   */
  function getValidationHost(form) {
    var host = form.closest('product-form, sticky-atc');
    if (host && host.matches('sticky-atc')) {
      var main = host.closest('.section-main-product')?.querySelector('product-form');
      if (main) return main;
    }
    return host;
  }

  /**
   * Whether this click provably leads to adding a real, purchasable variant. Fail-closed:
   * anything unproven is false.
   * @param {Element} btn - The theme's Add to Cart button.
   * @returns {boolean} Whether the upsell popup may open.
   */
  function isAddToCartValid(btn) {
    // Unavailable or sold-out: the theme disables its own button.
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return false;

    var form = btn.closest('.js-product-form, form[action*="/cart/add"]');
    if (!form) return false;

    // The theme's own validation - all required options chosen.
    var host = getValidationHost(form);
    if (host && typeof host.validate === 'function' && !host.validate()) return false;

    // A real variant id, not an empty or placeholder field.
    var input = form.querySelector('[name="id"]');
    var variantId = input && String(input.value || '').trim();
    return !!variantId && /^\d+$/.test(variantId);
  }

  window.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || !target.closest) return;

    var overlay = target.closest(OVERLAY);
    if (!overlay) return;

    var btn = getCoveredButton(overlay);
    if (btn && isAddToCartValid(btn)) return;

    // Cut the app's overlay listener off before it can call upsellInit().
    evt.stopImmediatePropagation();
    evt.preventDefault();

    // Hand the click to the theme so its own validation runs and shows its own error.
    if (btn) btn.click();
  }, true);
})();
